import React, { useState, useEffect, createContext, useContext } from 'react';
import * as Router from 'react-router-dom';
import * as Antd from 'antd';
import Moment from 'moment';

import Pages from './Pages';
import Login from './Login';
import Axios from 'axios';

import 'moment/locale/cs';
import { applicationStorage, cacheStorage, datasetgridColumnsStorage, datasetgridFiltersStorage } from '../storage';

export const AuthContext = createContext();

const PrivateRoute = ({ children, ...rest }) => {
  let auth = useContext(AuthContext);
  return (
    <Router.Route
      {...rest}
      render={({ location }) =>
        auth.isLogged ? (
          children
        ) : (
          <Router.Redirect
            to={{
              pathname: "/login",
              state: { from: location }
            }}
          />
        )
      }
    />
  );
}

export const useProvideAuth = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isLogged, setIsLogged] = useState(true);

  const signIn = async (credentials, cb) => {
    try {
      let signInResp = await Axios.post(`${process.env.REACT_APP_API_BASE_URI}/api/users/login`, credentials);
      localStorage.setItem('access_token', signInResp.data.accessToken);
      applicationStorage.setItem('accessToken', signInResp.data.accessToken);
      setAccessToken(signInResp.data.accessToken);
      applicationStorage.clear();
      cacheStorage.clear();
      datasetgridColumnsStorage.clear();
      datasetgridFiltersStorage.clear();
      setIsLogged(true);
      await check();
      return true;
    } catch (err) {
      setIsLogged(false);
      setAccessToken(null);
      setUserProfile(null);
      throw new Error('Invalid credential!');
    }
  };

  const signOut = (cb) => {
    localStorage.removeItem('access_token');
    applicationStorage.clear();
    cacheStorage.clear();
    datasetgridColumnsStorage.clear();
    datasetgridFiltersStorage.clear();
    setIsLogged(false);
    setAccessToken(null)
    setUserProfile(null);
  };

  const check = async () => {
    let accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      setIsLogged(false);
      setAccessToken(null);
      setUserProfile(null);
    }
    setIsLogged(true);
    setAccessToken(accessToken);

    try {
      let userResp = await Axios.get(`${process.env.REACT_APP_API_BASE_URI}/api/users/profile`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      setUserProfile(userResp.data);

    } catch (err) {
      setIsLogged(false);
      setAccessToken(null);
      setUserProfile(null);
    }
  };

  return {
    isLogged,
    userProfile,
    accessToken,
    signIn,
    signOut,
    check
  };
}

const App = () => {
  const [loading, setLoading] = useState(true);
  const auth = useProvideAuth();

  const init = async () => {
    try {
      // Set locale
      Moment.locale(window.navigator.userLanguage || window.navigator.language || 'en-GB');

      // Check authentication
      await auth.check();

      // Load app name
      let { data: publicData } = await Axios.get(`${process.env.REACT_APP_API_BASE_URI}/api/public`);
      window.document.title = publicData.applicationName;
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    setLoading(true);
    init();
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <Antd.Layout className="layout">
        <Antd.Spin
          tip="Application loading..."
          style={{
            marginTop: 100
          }}
        />
      </Antd.Layout>
    );
  } else {
    return (
      <AuthContext.Provider value={auth}>
        <Router.BrowserRouter>
          <Antd.Affix offsetTop={0}>
            {process.env.NODE_ENV === 'development'
              ? <Antd.Alert message="Development mode" banner />
              : null}
          </Antd.Affix>
          <Router.Switch>
            <Router.Route exact path="/login">
              <Login />
            </Router.Route>
            <PrivateRoute exact>
              <Pages />
            </PrivateRoute>
          </Router.Switch>
        </Router.BrowserRouter>
      </AuthContext.Provider>
    );
  }
}

export default App
